/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, IDetailsRowProps, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { ILinkStyleProps, ILinkStyles } from '@fluentui/react/lib/Link';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AppStore } from 'src/core/stores/app/AppStore';
import { AppType } from 'src/core/stores/app/types';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { getDateLocale } from 'src/core/utils/supportedLocales';

export type ProjectsTableProps = TableProps<AppStore> & {
    /**
     * Function that gets invoked when an app is clicked.
     */
    onAppClick: (app: AppStore) => void;

    isRowClickable?: boolean;
};

const messages = defineMessages({
    nameColumnHeader: {
        id: 'landingPage.projectsTable.nameColumnHeader',
        defaultMessage: 'Name'
    },
    typeColumnHeader: {
        id: 'landingPage.projectsTable.typeColumnHeader',
        defaultMessage: 'Type'
    },
    modifiedOnColumnHeader: {
        id: 'landingPage.projectsTable.modifiedOnColumnHeader',
        defaultMessage: 'Last updated'
    }
});

const { styleDiv } = getStylistV2('ProjectsTable');
const Row = styleDiv('Row', { cursor: 'pointer' });
const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { marginRight: '8px' } });
const nameLinkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({
    root: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: '14px !important',
        // The 24px belongs to 16px for the icon and 8px for the children gap.
        maxWidth: 'calc(100% - 24px)'
    }
});

const getReadableServiceTypeFromAppType = (appType: AppType) => {
    switch (appType) {
        case 'CustomEntityRecognition':
            return 'Custom named entity recognition';
        case 'CustomSingleLabelClassification':
            return 'Custom text single label classification';
        case 'CustomMultiLabelClassification':
            return 'Custom text multi label classification';
        case 'qnaMaker':
            return 'Custom question answering';
        case 'clu':
            return 'Conversational language understanding';
        case 'Orchestration':
            return 'Orchestration workflow';
        default:
    }
};

/**
 * Displays the table of projects for the language studio services.
 */
export const ProjectsTable = observer((props: ProjectsTableProps) => {
    const { items, sortKey, onAppClick, isRowClickable = true, ...rest } = props;

    const { localize } = useLocalization();
    const { uiStore } = useGlobalState();

    const onRenderRow = (defaultProps: IDetailsRowProps, defaultRenderer: (props: IDetailsRowProps) => JSX.Element) => (
        <Row onClick={_ => onAppClick(defaultProps.item as AppStore)}>{defaultRenderer(defaultProps)}</Row>
    );

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 300,
            isResizable: true,
            fieldName: 'name',
            name: localize(messages.nameColumnHeader),
            isSorted: sortKey?.key === 'name',
            styles: { cellTitle: { paddingLeft: '50px' } },
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: AppStore) => [
                <Icon iconName="Page" styles={iconStyles} />,
                <NameCellLink styles={nameLinkStyles} data-automation-id="appCell" onClick={() => onAppClick(item)}>
                    {item.name}
                </NameCellLink>
            ]
        },
        {
            minWidth: 300,
            key: 'appType',
            isResizable: true,
            fieldName: 'appType',
            name: localize(messages.typeColumnHeader),
            isSorted: sortKey?.key === 'appType',
            isSortedDescending: sortKey?.key === 'appType' && sortKey?.order === 'desc',
            onRender: (item: AppStore) => <LuisText variant="small">{getReadableServiceTypeFromAppType(item.appType)}</LuisText>
        },
        {
            minWidth: 300,
            isResizable: true,
            key: 'modifiedDate',
            fieldName: 'modifiedDate',
            name: localize(messages.modifiedOnColumnHeader),
            isSorted: sortKey?.key === 'modifiedDate',
            isSortedDescending: sortKey?.key === 'modifiedDate' && sortKey?.order === 'desc',
            onRender: (item: AppStore) => (
                <LuisText variant="small">
                    {item.modifiedDate ? formatDistanceToNow(item.modifiedDate, { locale: getDateLocale(uiStore.locale) }) : '-'}
                </LuisText>
            )
        }
    ];

    return (
        <Table
            items={items}
            columns={columns}
            onRenderRow={isRowClickable ? onRenderRow : undefined}
            selectionMode={SelectionMode.none}
            onRenderDetailsHeader={(headerProps, defaultRenderer) =>
                defaultRenderer({ ...headerProps, styles: { root: { paddingTop: 0 } } })
            }
            {...rest}
        />
    );
});
